/**
 * In the Capacitor Android WebView, ties the soft keyboard to
 * `window.increaseHeight` / `window.resetHeight` from index.html so #root can grow
 * and scroll. Does not use @capacitor/keyboard.
 */

const MIN_KEYBOARD_DROP_PX = 120;

function isCapacitorAndroid(): boolean {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return false;
  }
  if (!/Android/i.test(navigator.userAgent || "")) return false;
  const cap = (
    window as Window & { Capacitor?: { getPlatform?: () => string } }
  ).Capacitor;
  return cap?.getPlatform?.() === "android";
}

function callIncrease(): void {
  const fn = (window as Window & { increaseHeight?: (h?: number) => void })
    .increaseHeight;
  if (typeof fn === "function") {
    fn(window.innerHeight);
  }
}

function callReset(): void {
  const fn = (window as Window & { resetHeight?: () => void }).resetHeight;
  if (typeof fn === "function") {
    fn();
  }
}

/**
 * Call once at startup (e.g. from main.tsx). No-op on non–Android or without visualViewport.
 */
export function installAndroidKeyboardHeightBridge(): void {
  if (!isCapacitorAndroid()) return;
  const vv = window.visualViewport;
  if (!vv) return;

  let keyboardOpen = false;
  /** Tallest visual viewport height seen while keyboard was considered closed. */
  let closedVvHeight = vv.height;

  const sync = (): void => {
    const h = vv.height;
    if (!keyboardOpen) {
      closedVvHeight = Math.max(closedVvHeight, h);
    }

    const drop = closedVvHeight - h;
    if (!keyboardOpen && drop >= MIN_KEYBOARD_DROP_PX) {
      keyboardOpen = true;
      callIncrease();
      return;
    }

    if (keyboardOpen && h >= closedVvHeight - 40) {
      keyboardOpen = false;
      closedVvHeight = h;
      callReset();
    }
  };

  const onVvChange = (): void => {
    requestAnimationFrame(sync);
  };

  vv.addEventListener("resize", onVvChange);
  vv.addEventListener("scroll", onVvChange);

  window.addEventListener("orientationchange", () => {
    keyboardOpen = false;
    closedVvHeight = vv.height;
    callReset();
  });
}
